<script lang="ts">
	import { t } from '$i18n/i18n'
	import type { Lot } from './lot'
	import { createEventDispatcher, getContext } from 'svelte'
	import { CalcMethod, OfferViewMode, TooltipPlacements } from '$types/enums'
	import OptimizerDecisionButtons from '../bid/OptimizerDecisionButtons.svelte'
	import OfferInfo from '../bid/OfferInfo.svelte'
	import PrimaryButton, { PrimaryBtnColors } from '../atoms/PrimaryButton.svelte'
	import AcceptOfferComponent from '../bid/AcceptOfferComponent.svelte'
	import RejectOfferComponent from './RejectOfferComponent.svelte'
	import OfferFactsForm from '../bid/OfferFactsForm.svelte'
	import { type Offers } from '../bid/types/offer'
	import { type Writable } from 'svelte/store'
	import OptimizerPagesTemplate from './OptimizerPagesTemplate.svelte'
	import type { ContactData } from '$types/contactData'
	import { anyOfferAlreadyAccepted, checkEmptyOffers } from '../bid/offerUtils'

	const dispatch = createEventDispatcher()

	let lot: Writable<Lot> = getContext('lot')
	let offers: Writable<Offers> = getContext('offers')
	const reloadRequest: () => Promise<void> = getContext('reloadRequest')

	export let optimizerContactData: ContactData

	let offerViewMode: OfferViewMode = OfferViewMode.VIEW_OFFER

	let emptyOffers: boolean
	let editOfferButtonText: string
	let offerAlreadyAccepted: boolean

	const viewOffer = () => (offerViewMode = OfferViewMode.VIEW_OFFER)
	const viewEditOffer = () => (offerViewMode = OfferViewMode.EDIT_OFFER)
	const viewAcceptOffer = () => (offerViewMode = OfferViewMode.ACCEPT_OFFER)
	const viewRejectOffer = () => (offerViewMode = OfferViewMode.REJECT_OFFER)
	const offerChanged = () => dispatch('offerChanged')
	// reload before leaving edit mode, so the view mounts with fresh data
	const cancelEdit = async () => {
		try {
			await reloadRequest?.()
			viewOffer()
		} catch {
			/**
			 * reload failed (already surfaced via notification) - stay in edit mode rather than
			 * switching to a view that may not reflect the discarded local edits
			 */
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: ($offers,
		$offers.material,
		$offers.materialWithTransport,
		$offers.transport,
		(emptyOffers = checkEmptyOffers($offers)))

	$: editOfferButtonText =
		$lot.calcMethod === CalcMethod.REQUEST_OFFER
			? $t('UI.optimizerPage.priceOffer.editor.title.edit')
			: $t('UI.optimizerPage.counterOffer.editor.title.edit')
	$: offerAlreadyAccepted = anyOfferAlreadyAccepted($offers)
</script>

<OptimizerPagesTemplate
	recipientData={optimizerContactData}
	let:linkAttachments={linkAttachmentsByAccept}
	on:offerChanged={offerChanged}
>
	{#if offerViewMode === OfferViewMode.VIEW_OFFER}
		{#if !emptyOffers}
			<div class="col-lg-12 col-sm-12 columns" style="margin-top: 1.875rem">
				<OfferInfo onOptimizerPage />
				<!-- only show the button to edit the offer when we don't already have accepted offers -->
				<div class="align-right">
					<PrimaryButton
						label={editOfferButtonText}
						color={PrimaryBtnColors.ORANGE}
						disabled={offerAlreadyAccepted}
						info={offerAlreadyAccepted ? 'Angebot wurde bereits angenommen' : null}
						infoPlacement={TooltipPlacements.LEFT}
						on:click={viewEditOffer}
					/>
				</div>
			</div>
		{:else}
			<OptimizerDecisionButtons
				calcMethod={$lot.calcMethod!}
				onAcceptOffer={viewAcceptOffer}
				onDeclineOffer={viewRejectOffer}
				onEditOffer={viewEditOffer}
			/>
		{/if}
	{/if}

	{#if offerViewMode === OfferViewMode.ACCEPT_OFFER}
		<AcceptOfferComponent
			on:cancel={viewOffer}
			on:offerChanged={offerChanged}
			on:linkAttachmentsByAccept={linkAttachmentsByAccept}
		/>
	{/if}

	{#if offerViewMode === OfferViewMode.EDIT_OFFER}
		<OfferFactsForm
			on:cancelEdit={cancelEdit}
			on:linkAttachmentsByAccept={linkAttachmentsByAccept}
			on:offerChanged={() => {
				offerChanged()
				viewOffer()
			}}
		/>
	{/if}

	{#if offerViewMode === OfferViewMode.REJECT_OFFER}
		<RejectOfferComponent isTooltipEnable on:cancel={viewOffer} on:offerChanged={offerChanged} />
	{/if}
</OptimizerPagesTemplate>

<style lang="scss">
	@import '$styles/pubPage';
</style>
